<template>
  <main class="p-mailaddressregistration">
    <section class="p-mailaddressregistration__wrap">
      <!-- 設定メール送信完了 -->
      <template v-if="state.isSendComplete">
        <h2 class="c-regist__title caution">{{ t('MailAddressRegistrationPage.title2') }}</h2>
        <p class="p-mailaddressregistration__comment">
          {{ t('MailAddressRegistrationPage.message4') }}
        </p>
        <p class="p-mailaddressregistration__caution">
          {{ t('MailAddressRegistrationPage.caution') }}
        </p>
      </template>
      <!-- メールアドレス入力画面 -->
      <template v-else>
        <h2 class="c-regist__title">{{ t('MailAddressRegistrationPage.title') }}</h2>
        <p class="p-mailaddressregistration__comment">
          {{ t('MailAddressRegistrationPage.message1') }}
        </p>
        <p class="p-mailaddressregistration__description">
          {{ t('MailAddressRegistrationPage.message2') }}
        </p>
        <p class="p-mailaddressregistration__description">
          {{ t('MailAddressRegistrationPage.message3') }}
        </p>
        <dl class="p-mailaddressregistration__lists">
          <div class="p-mailaddressregistration__list">
            <dt class="p-mailaddressregistration__list-title">
              <span class="-text">{{ t('MailAddressRegistrationPage.mailAddress') }}</span>
            </dt>
            <dd>
              <input-text
                ref="inFieldMailAddress"
                v-model:input-value="registerTeamStore.mailAddress"
                class="p-mailaddressregistration__list-data-item"
                name="mailAddress"
                placeholder="sample@ex.co.jp"
                rules="required|emailFormat"
                :otherErrorMessage="
                  state.isDuplicateMailAddress
                    ? [t('AuthenticateUserPage.alreadyInUseMailAddressMessage')]
                    : []
                "
                @input="
                  () => {
                    state.isAvailableMailAddress = false
                    state.isDuplicateMailAddress = false
                  }
                "
                @delayinput="checkExistMailAddress"
              />
            </dd>
          </div>
        </dl>
        <div class="p-confirmation__link-wrap">
          <div class="p-confirmation__link">
            <router-link
              :to="{ name: 'AddTeamPage', query: { tokenId: enterAccountStore.tokenId } }"
              class="c-button"
            >
              <span class="c-button__text">{{ t('MailAddressRegistrationPage.back') }}</span>
            </router-link>
          </div>
          <div class="p-confirmation__link">
            <button @click="onClickSendMail" :disabled="!isEnableButton" class="c-button">
              <span class="c-button__text">{{ t('MailAddressRegistrationPage.confirm') }}</span>
            </button>
          </div>
        </div>
      </template>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref, reactive, nextTick, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import StoreUtil from '@/stores/StoreUtil'
import InputText from '@/components/common/field/InputText.vue'
import Utility from '@/util/Utility'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'
import useEnterAccount from './hook/useEnterAccount'

export default defineComponent({
  name: 'MailAddressRegistrationPage',
  setup() {
    const router = useRouter()
    const enterAccountStore = StoreUtil.useStore('EnterAccountStore')
    const messageDialogStore = StoreUtil.useStore('MessageDialogStore')
    const registerTeamStore = StoreUtil.useStore('RegisterTeamStore')
    const MailAddressRegistrationStore = StoreUtil.useStore('MailAddressRegistrationStore')
    const ErrorStore = StoreUtil.useStore('ErrorStore')
    const loginStore = StoreUtil.useStore('LoginStore')
    const { enterAccount } = useEnterAccount()
    const { checkMaintenanceStatusByApiBaseUrl } = useMaintenanceStatus()
    const inFieldMailAddress = ref<InstanceType<typeof InputText>>()
    const { t } = useI18n()
    const state = reactive({
      isAvailableMailAddress: false,
      isDuplicateMailAddress: false,
      isSendComplete: false,
    })

    const isEnableButton = computed(
      () =>
        inFieldMailAddress.value?.meta.valid &&
        state.isAvailableMailAddress &&
        !state.isDuplicateMailAddress,
    )

    /**
     * beforeunload のイベントリスナー
     * @param {BeforeUnloadEvent} event beforeunloadのイベントの引数
     */
    const beforeUnloadEventListener = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      // eslint-disable-next-line no-param-reassign
      event.returnValue = ''
    }

    /**
     * メール送信ボタンを押したときの処理
     * @return {Promise<void>} 処理完了を待機するためのPromise
     */
    const onClickSendMail = async () => {
      const res = await MailAddressRegistrationStore.sendMailAddressChanged(
        registerTeamStore.mailAddress,
      )

      if (!res.isSuccess) {
        // メール送信失敗時にダイアログを表示する
        await messageDialogStore.openWithSettings({
          title: t('MailAddressRegistrationPage.sendErrorDialog.title'),
          message: t('MailAddressRegistrationPage.sendErrorDialog.message'),
          status: 'error',
          cancelButton: false,
        })
        state.isSendComplete = false
      } else {
        state.isSendComplete = true
        // 離脱防止用
        window.addEventListener('beforeunload', beforeUnloadEventListener, false)

        // メールアドレスが登録完了したか確認するためのポーリング
        do {
          enterAccountStore.fetchAccessToken()
          // eslint-disable-next-line no-await-in-loop
          await Utility.sleep(1000)
        } while (!loginStore.mailAddress)

        await enterAccount()
      }
    }

    /**
     * メールアドレスが既に登録されているものかどうか判定する
     * @return {Promise<void>} 処理完了を待機するためのPromise
     */
    const checkExistMailAddress = async () => {
      state.isAvailableMailAddress = false
      await nextTick()
      if (!inFieldMailAddress.value?.value) return
      await inFieldMailAddress.value?.validate()
      if (!inFieldMailAddress.value?.meta.valid) return
      // メールアドレスの重複チェックを実行する
      const result = await registerTeamStore.checkDuplicateMailAllRegion()
      if (result.isValidMainAddress) {
        state.isDuplicateMailAddress = false
        state.isAvailableMailAddress = true
      } else if (result.isDuplicateMail) {
        state.isDuplicateMailAddress = true
        state.isAvailableMailAddress = false
      } else {
        // ネットワークエラーなどでチェックができなかったときの処理
        state.isDuplicateMailAddress = false
        state.isAvailableMailAddress = false
      }
    }

    onMounted(async () => {
      const isAvailable = await checkMaintenanceStatusByApiBaseUrl()
      if (!isAvailable) {
        router.replace({ name: 'MaintenancePage' })
        return
      }
      // ログイン情報がない、もしくはメールアドレス設定済みの場合はエラーページに飛ばす
      if (!loginStore.isLogin || !!loginStore.mailAddress) {
        ErrorStore.setConfig('directAccessError')
        router.replace({ name: 'ErrorPage' })
        return
      }
      enterAccountStore.isInvitedUser = true
      enterAccountStore.isExistingAccount = true
      if (typeof router.currentRoute.value.query.tokenId !== 'string') {
        ErrorStore.setConfig('tokenInvalid.invite')
        router.replace({ name: 'ErrorPage' })
        return
      }
      enterAccountStore.tokenId = router.currentRoute.value.query.tokenId
      // token が有効化チェックする
      const validationResult = await enterAccountStore.validateToken()
      if (!validationResult.isSuccess || !validationResult.res.data.isValid) {
        await messageDialogStore.openWithSettings({
          title: t('EnterAccountPage.urlInvalidError.title'),
          message: t('EnterAccountPage.urlInvalidError.messageForInvite'),
          status: 'error',
        })
        ErrorStore.setConfig('tokenInvalid.invite')
        router.replace({ name: 'ErrorPage' })
      }
      checkExistMailAddress()
    })

    onUnmounted(() => {
      window.removeEventListener('beforeunload', beforeUnloadEventListener, false)
    })

    return {
      state,
      isEnableButton,
      enterAccountStore,
      registerTeamStore,
      inFieldMailAddress,
      t,
      onClickSendMail,
      checkExistMailAddress,
    }
  },
  components: {
    InputText,
  },
})
</script>

<style scoped lang="scss">
.c-button:disabled {
  opacity: 0.3;
  pointer-events: none;
}
.caution {
  color: #e30f0f;
}
</style>
